import React from "react";
import { Icon, Portal, LogoMark } from "./registry";
import { expiryOf, expiredSignMessage, businessTz, isYmd } from "./lib/quotedetails";   // the sandbox signs by the server's rules
/* ============================================================
   Fence Works — demo mode (sandbox) UI
   Banner + reset, "Keep this as my free workspace", SAMPLE watermark,
   expiry / switched-off screens, and the hub's local sandbox actions.
   Everything reads window.VFP_DEMO (lib/demo.ts).
   ============================================================ */
const D = (): any => (window as any).VFP_DEMO || { active: false };

/* ---- hub actions on the visitor's own device: sign + mock deposit write
   straight into the sandbox store (localStorage), which the app tab picks up
   via the storage event / on its next load. No server, no Stripe. ---- */
function demoHubAction(token: string, path: string, body: any) {
  const d = D();
  let st: any = null;
  try { st = JSON.parse(localStorage.getItem(d.stateKey) || "null"); } catch (e) {}
  if (!st) throw new Error("Sandbox session not found — open the sandbox again.");
  const write = () => { try { localStorage.setItem(d.stateKey, JSON.stringify(st)); } catch (e) {} };
  // Messaging is per-customer, not per-quote — handle it before the quote lookup.
  // The write lands in the same sandbox state the app reads, so a note sent from
  // the hub shows up on the contractor's Messages page (and vice versa).
  if (path === "message") {
    const cust = (st.customers || []).find((c: any) => c && c.hubToken === token);
    if (!cust) throw new Error("Customer not found");
    const text = String((body && body.body) || "").trim();
    const image = body && typeof body.image === "string" ? body.image.slice(0, 200000) : null;
    if (!text && !image) throw new Error("Nothing to send");
    if (!st.threads) st.threads = {};
    st.threads[cust.id] = [...(st.threads[cust.id] || []), { from: "customer", text, image, at: Date.now() }];
    write();
    d.track && d.track("hub_message_sent", { where: "local" });
    return { ok: true };
  }
  // photos from the hub's "Send us photos" card: onto the customer's job when
  // there is one (beside the crew's photos, tagged from the customer), else into
  // the thread as pictures — the same places the service puts them
  if (path === "photos") {
    const cust = (st.customers || []).find((c: any) => c && c.hubToken === token);
    if (!cust) throw new Error("Customer not found");
    const photos = Array.isArray(body && body.photos) ? body.photos.filter((p: any) => typeof p === "string" && p.startsWith("data:image/")).slice(0, 6).map((p: string) => p.slice(0, 400000)) : [];
    if (!photos.length) throw new Error("No photos");
    const at = Date.now();
    const wo = (st.workOrders || []).find((w: any) => w && w.id === (body && body.workOrderId) && w.customerId === cust.id) || (st.workOrders || []).find((w: any) => w && w.customerId === cust.id);
    if (wo) {
      wo.photos = [...(wo.photos || []), ...photos.map((url: string) => ({ name: "From the customer", url, at, tag: null, by: "customer", web: false }))];
      write(); d.track && d.track("hub_photos_sent", { where: "local", to: "job" });
      return { ok: true, urls: photos, attachedTo: "job", workOrderId: wo.id };
    }
    if (!st.threads) st.threads = {};
    st.threads[cust.id] = [...(st.threads[cust.id] || []), ...photos.map((image: string, i: number) => ({ from: "customer", text: i === 0 && body.note ? String(body.note).slice(0, 400) : "", image, at: at + i }))];
    write(); d.track && d.track("hub_photos_sent", { where: "local", to: "thread" });
    return { ok: true, urls: photos, attachedTo: "customer" };
  }
  const q = (st.quotes || []).find((x: any) => x && x.id === (body && body.quoteId));
  if (!q) throw new Error("Quote not found");
  if (path === "sign") {
    if (!body.agree) throw new Error("Consent checkbox is required");
    if (!body.name || String(body.name).trim().length < 2) throw new Error("Type your full legal name");
    if (q.signature && q.signature.at) return { ok: true, already: true, signedAt: q.signature.at };
    // the server's rule (esign.js): an expired quote can't be accepted
    const ex = expiryOf(q.validUntil, { tz: businessTz(st.settings) });
    if (ex.expired) throw new Error(expiredSignMessage(ex.validUntil, { company: (st.company && st.company.name) || (st.settings && st.settings.companyName) }));
    q.signature = { name: String(body.name).trim(), image: typeof body.image === "string" ? body.image.slice(0, 200000) : null, at: new Date().toISOString(), terms: body.termsVersion || null, sandbox: true };
    if (q.status !== "paid") q.status = "signed";
    q.signedTotal = q.signedTotal != null ? q.signedTotal : ((q.customerDoc && q.customerDoc.quote && q.customerDoc.quote.customerTotal) || q.total);
    // frozen with the note and the date it was signed under, as the server does
    if (!q.signedDoc && q.customerDoc && q.customerDoc.lines) q.signedDoc = { at: Date.now(), ...q.customerDoc.quote, lines: q.customerDoc.lines, customerTotal: q.signedTotal,
      ...(typeof q.customerDoc.coverNote === "string" && q.customerDoc.coverNote ? { coverNote: q.customerDoc.coverNote } : {}),
      ...(isYmd(q.validUntil) ? { validUntil: q.validUntil } : {}) };
    write();
    d.track && d.track("customer_signed", { where: "local" });
    return { ok: true, signedAt: q.signature.at };
  }
  if (path === "checkout") {
    const total = Number(q.total) || 0;
    const manual = Array.isArray(q.payments) ? q.payments.reduce((s: number, p: any) => s + (Number(p.amount) || 0), 0) : 0;
    const paid = (Number(q.depositPaid) || 0) + manual;
    const remaining = Math.max(0, total - paid);
    let amount: number, kind: string;
    if (body.pct === "balance") { amount = remaining; kind = "balance"; }
    else { const p = Math.max(1, Math.min(100, Number(body.pct) || 50)); amount = Math.min(total * (p / 100), remaining); kind = (p >= 100 || amount >= remaining - 0.005) ? "full" : "deposit"; }
    amount = Math.round(Math.min(amount, remaining) * 100) / 100;
    if (amount < 0.5) throw new Error("This estimate is already paid in full.");
    q.depositPaid = (Number(q.depositPaid) || 0) + amount;
    q.status = "paid";
    q.signedTotal = q.signedTotal != null ? q.signedTotal : total;
    q.stripePayments = [...(q.stripePayments || []), { amount, method: "stripe", source: "sandbox", kind, at: Date.now(), sessionId: "cs_demo_local" }];
    write();
    d.track && d.track("deposit_mocked", { where: "local", kind });
    return { url: "/hub/" + encodeURIComponent(token) + "?paid=1" };
  }
  if (path === "approve-co") {
    if (!body.agree) throw new Error("Consent checkbox is required");
    if (!body.name || String(body.name).trim().length < 2) throw new Error("Type your full legal name");
    const cos = Array.isArray(q.changeOrders) ? q.changeOrders : [];
    const co = cos.find((c: any) => c && c.id === body.coId);
    if (!co) throw new Error("Change order not found");
    if (co.status === "approved") return { ok: true, already: true };
    co.status = "approved"; co.approval = { name: String(body.name).trim(), at: new Date().toISOString(), sandbox: true };
    const base = q.signedTotal != null ? Number(q.signedTotal) : Number(q.total) || 0;
    q.total = Math.round((base + cos.filter((c: any) => c.status === "approved").reduce((s: number, c: any) => s + (Number(c.amount) || 0), 0)) * 100) / 100;
    write();
    d.track && d.track("change_order_approved", { where: "local" });
    return { ok: true, approvedAt: co.approval.at, revisedTotal: q.total };
  }
  if (path === "confirm") return { ok: true };
  throw new Error("That action isn't part of the sandbox.");
}

function DemoWatermark({ fixed }: any) {
  return <div className={"demo-watermark" + (fixed ? " fixed" : "")} aria-hidden="true"><span>SAMPLE</span></div>;
}

/* slim bar on the customer hub — the visitor is looking at "what my customer
   gets"; say so, and route them back to the sandbox */
function DemoHubBar({ local }: any) {
  return (
    <div className="demo-hub-bar">
      <b>Sandbox</b><span>This is exactly what your customer receives. Nothing here is charged or saved.</span>
      {local && <a className="btn btn-ghost btn-sm" href="/">← Back to the sandbox</a>}
    </div>
  );
}

function minsLeft() {
  try { const s = JSON.parse(localStorage.getItem("fw_demo_v1") || "null"); return s ? Math.max(0, Math.round((Number(s.expiresAt) - Date.now()) / 60000)) : 0; } catch (e) { return 0; }
}

function DemoBanner({ store, go }: any) {
  const d = D();
  const [keep, setKeep] = React.useState<any>(false);
  const [server, setServer] = React.useState<any>(d.server);
  const [, tick] = React.useState<any>(0);
  React.useEffect(() => {
    const h = (e: any) => setServer({ ...(e.detail || d.server) });
    window.addEventListener("fw:demo-server", h);
    const t = setInterval(() => tick((n: number) => n + 1), 60000);
    return () => { window.removeEventListener("fw:demo-server", h); clearInterval(t); };
  }, []);
  const left = minsLeft();
  return (
    <>
      <div className="demo-bar" role="status">
        <div className="demo-bar-msg">
          <b>Sandbox</b>
          <span>Nothing here is saved.{left > 0 ? ` Clears after ${left} min idle.` : ""}</span>
          {server && server.busy && <span className="demo-chip" title="Email and the phone link are paused for this session — everything else works">Busy — email off, try the sample yard</span>}
        </div>
        <div className="demo-bar-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => d.reset()} title="Drop everything and start a fresh sandbox" aria-label="Start over"><Icon name="undo" size={13} /><span className="demo-bar-long">Start over</span></button>
          <button className="btn btn-primary btn-sm" onClick={() => setKeep(true)} title="Create your free account with the work you did here"><Icon name="check" size={13} /><span className="demo-bar-long">Keep this as my free workspace</span><span className="demo-bar-short">Keep my work</span></button>
          <button className="btn btn-ghost btn-sm" onClick={() => d.exit()} aria-label="Leave the sandbox" title="Leave the sandbox"><Icon name="x" size={13} /></button>
        </div>
      </div>
      {keep && <KeepWorkspaceModal store={store} onClose={() => setKeep(false)} />}
    </>
  );
}

/* create the real Free account from inside the sandbox; App applies the
   carried state after the new workspace hydrates (lib/demo.ts carryState) */
function KeepWorkspaceModal({ store, onClose }: any) {
  const d = D();
  const co = (store && store.company) || {};
  const typed = String(co.name || (store && store.settings && store.settings.companyName) || "").trim();
  const [name, setName] = React.useState<any>("");
  const [company, setCompany] = React.useState<any>(d.company || (typed && typed !== d.seedCompany ? typed : ""));
  const [email, setEmail] = React.useState<any>(d.email || "");
  const [pw, setPw] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const [done, setDone] = React.useState<any>("");
  const kept = (() => { try { const c = d.carryState(company); return c ? { quotes: (c.quotes || []).length, customers: (c.customers || []).length, workOrders: (c.workOrders || []).length } : { quotes: 0, customers: 0, workOrders: 0 }; } catch (e) { return { quotes: 0, customers: 0, workOrders: 0 }; } })();
  async function submit(e: any) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(email || "").trim())) throw new Error("Enter a valid work email.");
      if (String(pw || "").length < 8) throw new Error("Use at least 8 characters for your password.");
      const r = await d.promote({ email: String(email).trim(), password: pw, name: String(name || "").trim(), company: String(company || "").trim() });
      if (r && r.session) { window.location.href = "/"; return; }
      setDone("Account created — check your email to confirm it, then sign in. Your sandbox work will be waiting in your workspace.");
    } catch (e2: any) {
      setErr(String((e2 && (e2.message || e2.error_description)) || "Something went wrong"));
    } finally { setBusy(false); }
  }
  return (
    <Portal>
      <div className="modal-scrim" data-dialog onClick={onClose}>
        <form className="modal demo-modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
          <div className="row gap-8" style={{ alignItems: "center", marginBottom: 6 }}><LogoMark size={26} /><span className="kicker kicker-teal">Free plan · no card</span></div>
          <h3 className="display" style={{ fontSize: 20, margin: "0 0 6px" }}>Keep this as my free workspace</h3>
          <p className="muted" style={{ fontSize: 13, margin: "0 0 14px" }}>
            Your estimate{kept.quotes === 1 ? "" : "s"}, customer{kept.customers === 1 ? "" : "s"}, work order{kept.workOrders === 1 ? "" : "s"}, price book and crew come with you. The sample lead and sample jobs stay here.
            {kept.quotes + kept.customers + kept.workOrders > 0 && <> <b style={{ color: "var(--fg-2)" }}>{kept.quotes} quote{kept.quotes === 1 ? "" : "s"} · {kept.customers} customer{kept.customers === 1 ? "" : "s"} · {kept.workOrders} work order{kept.workOrders === 1 ? "" : "s"}</b> ready to move.</>}
          </p>
          {done ? <div className="auth-msg ok">{done}</div> : (
            <>
              <div style={{ display: "grid", gap: 10 }}>
                <div><label className="field-label">Your name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Sam Carter" autoComplete="name" /></div>
                <div><label className="field-label">Company</label><input className="input" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Evergreen Fence Co." autoComplete="organization" /></div>
                <div><label className="field-label">Work email</label><input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" /></div>
                <div><label className="field-label">Password</label><input className="input" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" autoComplete="new-password" /></div>
              </div>
              {err && <div className="auth-msg err" style={{ marginTop: 10 }}>{err}</div>}
              <div className="row gap-8" style={{ marginTop: 14 }}>
                <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? "Creating…" : "Create my free workspace"}</button>
                <button className="btn btn-ghost" type="button" onClick={onClose}>Not now</button>
              </div>
              <p className="faint" style={{ fontSize: 11.5, margin: "12px 0 0" }}>Free forever: 5 quotes a month, 3 products, satellite estimating. Upgrade any time.</p>
            </>
          )}
        </form>
      </div>
    </Portal>
  );
}

/* the sliding 30-minute window lapsed (lib/demo.ts dispatches fw:demo-expired) */
function DemoExpired() {
  const [show, setShow] = React.useState<any>(false);
  React.useEffect(() => {
    const h = () => setShow(true);
    window.addEventListener("fw:demo-expired", h); return () => window.removeEventListener("fw:demo-expired", h);
  }, []);
  if (!show) return null;
  return (
    <Portal>
      <div className="modal-scrim" data-dialog>
        <div className="modal demo-modal-card" style={{ textAlign: "center" }}>
          <LogoMark size={36} />
          <h3 className="display" style={{ fontSize: 20, margin: "12px 0 6px" }}>Your sandbox cleared</h3>
          <p className="muted" style={{ fontSize: 13.5, margin: "0 0 16px" }}>It sat idle for 30 minutes, so it's gone — nothing here is ever saved. Start a fresh one in one tap, or keep the next one as your free workspace.</p>
          <div className="row gap-8" style={{ justifyContent: "center" }}>
            <a className="btn btn-primary" href="/demo?reset=1">Start over</a>
            <a className="btn btn-ghost" href="https://fenceworks.ai/signup">Start free</a>
          </div>
        </div>
      </div>
    </Portal>
  );
}

/* the server says the sandbox is switched off (DEMO_MODE) */
function DemoOff() {
  return (
    <div className="demo-full">
      <div className="card pad" style={{ textAlign: "center", maxWidth: 440, padding: "40px 28px" }}>
        <LogoMark size={40} />
        <h2 className="display" style={{ fontSize: 22, margin: "14px 0 6px" }}>The sandbox isn't open right now</h2>
        <p className="muted" style={{ fontSize: 13.5, margin: "0 0 18px" }}>You can still start a free account — it takes about a minute and there's no card.</p>
        <div className="row gap-8" style={{ justifyContent: "center" }}>
          <a className="btn btn-primary" href="https://fenceworks.ai/signup">Start free</a>
          <button className="btn btn-ghost" onClick={() => D().exit()}>Sign in</button>
        </div>
      </div>
    </div>
  );
}

/* the sandbox thread for this hub's customer, in the app's own shape
   ({ from: "customer" | "company", text, image, at }) */
function demoHubThread(token: string) {
  const d = D();
  try {
    const st = JSON.parse(localStorage.getItem(d.stateKey) || "null");
    if (!st) return [];
    const cust = (st.customers || []).find((c: any) => c && c.hubToken === token);
    return (cust && (st.threads || {})[cust.id]) || [];
  } catch (e) { return []; }
}

Object.assign(window, { DemoBanner, DemoWatermark, DemoHubBar, DemoExpired, DemoOff, demoHubAction, demoHubThread });

export { DemoBanner, KeepWorkspaceModal, DemoWatermark, DemoHubBar, DemoExpired, DemoOff, demoHubAction, demoHubThread };
